'use client';

import ArticlePagination from '@/components/articlePagination/ArticlePagination';
import ArticleStage from '@/components/articleStage/ArticleStage';
import HeadlineField from '@/components/customFields/HeadlineField';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import { type GenericBlok, HeadlineTag } from '@/components/customFields/types';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import NestedBlocks from '@/components/nestedBlocks/NestedBlocks';
import type { PillCardProps } from '@/components/pillCard/PillCard';
import { RichText } from '@/components/richText/RichText';
import { getFormattedDate } from '@/helper/dates';
import { linkToString } from '@/helper/urls';
import classes from '@/storyblok/PageMagArticle/PageMagArticle.module.scss';
import type { PageMagArticleStoryblok } from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';
import { useTranslation } from 'react-i18next';

export type PageMagArticleProps = {
  relatedArticles?: PillCardProps[];
  relatedPressArticles?: PillCardProps[];
  breadcrumbs: ISbStoryData<GenericBlok>[];
  firstPublishedAt: string;
  updatedAt?: string;
  slug: string;
  story: ISbStoryData<PageMagArticleStoryblok>;
  blok?: PageMagArticleStoryblok;
};

export const PageMagArticle: React.FC<PageMagArticleProps> = (props) => {
  const { t } = useTranslation();

  const author = props.blok?.author;

  const date = props.updatedAt
    ? `${t('Zuletzt aktualisiert am')}: ${getFormattedDate(props.updatedAt)}`
    : getFormattedDate(props.firstPublishedAt);

  const stageFooter = author && typeof author !== 'string' && (
    <div className={classes.stageFooter}>
      <StoryblokImage image={author.content.image} />
      <span className={classes.text}>
        <InlineTextLink href={linkToString(author?.full_slug)} className={classes.authorName}>
          {author.content.first_name} {author.content.last_name}
        </InlineTextLink>
        <span className={classes.date}>{date}</span>
      </span>
    </div>
  );

  return (
    <div data-testid="magazine-article-page">
      {props.blok && (
        <ArticleStage
          linkLastElementInBreadcrumb={true}
          breadcrumbs={props.breadcrumbs}
          article={props.blok}
          footerContent={stageFooter}
          withTableOfContents
        />
      )}
      <div className="grid-wrapper">
        <div className="content-bloks-wrapper">
          <section className="article-summary">
            <RichText text={props.blok?.introduction} />
          </section>

          <NestedBlocks blocks={props.blok?.content} />

          {props.relatedArticles && props.relatedArticles?.length > 0 && (
            <section className="article-more">
              <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
                {t('magazine.relatedArticles')}
              </HeadlineField>
              <ArticlePagination baseUrl="/magazin/" articles={props.relatedArticles} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
export default PageMagArticle;
